import React, { useState, useRef, useEffect } from "react";
import logo from "../../../../assets/images/LOGO.png";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, fetchUserRole } from "../../../../redux/slices/userSlice";
import { validateEmail, validatePassword } from "../../../../utility/helper";
import { Toast } from "primereact/toast";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { AuthInputField } from "../../../../components/Stepper/fields/inputField";
const LoginForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { id } = useSelector((state) => state.user);
  const [rememberMe, setRememberMe] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const toast = useRef(null);
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit(e);
    }
  };
  
  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({ email: "", password: "" });
    let formIsValid = true;
    if (!email) {
      setErrors((prev) => ({ ...prev, email: "Email is required" }));
      formIsValid = false;
    } else if (!validateEmail(email)) {
      setErrors((prev) => ({ ...prev, email: "Invalid email format" }));
      formIsValid = false;
    }
    if (!password) {
      setErrors((prev) => ({ ...prev, password: "Password is required" }));
      formIsValid = false;
    } else if (!validatePassword(password)) {
      setErrors((prev) => ({
        ...prev,
        password:
          "Password must be at least 8 characters long, include at least one uppercase letter, one number and one special character",
      }));
      formIsValid = false;
    }
    if (formIsValid) {
      setLoading(true);
      try {
        const loginResponse = await dispatch(
          loginUser({ email, password })
        ).unwrap();
        const accessToken = loginResponse.access_token;

        if (rememberMe) {
          localStorage.setItem("rememberedEmail", email);
        } else {
          localStorage.removeItem("rememberedEmail");
        }

        const userRoleResponse = await dispatch(
          fetchUserRole(accessToken)
        ).unwrap();
        const lskey = `skip_2fa_${userRoleResponse.id}`;
        const skippedUser = JSON.parse(localStorage.getItem(lskey, false));
        if (userRoleResponse.status === 401) {
          navigate("/signin");
        } else if (userRoleResponse.role === "user") {
          if (!skippedUser) {
            navigate("/authentication");
          } else {
            navigate("/");
          }
        } else {
          navigate("/");
        }
      } catch (error) {
        console.error("Error during login:", error.message);
        toast.current.show({
          className: "bg-yellow-700 text-white",
          severity: "warn",
          summary: "Warning",
          detail: error.message,
          life: 3000,
        });
        setErrors((prev) => ({
          ...prev,
          email: "Login failed. Please check your credentials.",
        }));
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_20%_10%,_#5f5f5f,_#0b0b0b,_#000000,_#000000)] relative">
      <Toast ref={toast} />
      <div className="absolute top-1 left-4">
        <img src={logo} alt="App Icon" className="w-[4.5rem]" />
      </div>
      <div className="flex flex-col items-center min-h-screen">
        <div className="text-white text-center p-8 space-y-6 mt-12">
          <h1 className="text-2xl mb-20 text-amber-50">
            <p className="mb-4 text-primary-gradient">
              Welcome to Asset-Clarity!
            </p>
            <p className="text-primary-gradient">
              Log in to access your portfolio
            </p>
          </h1>
          <form className="min-w-[32vw] mx-auto" onSubmit={handleSubmit}>
            <AuthInputField
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="User Name"
              error={errors.email}
            />

            <div className="mb-5 relative">
              <AuthInputField
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Password"
                error={errors.password}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-lg text-gray-500"
              >
                {showPassword ? (
                  <FaEye className="text-primary" />
                ) : (
                  <FaEyeSlash />
                )}
              </button>
            </div>
            <div className="flex items-start mb-5">
              <div className="flex items-center h-5">
                <input
                  id="remember"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-blue-300 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-blue-600 dark:ring-offset-gray-800"
                />
              </div>
              <label
                htmlFor="remember"
                className="ms-2 text-sm font-medium text-gray-400 dark:text-gray-300"
              >
                Remember me
              </label>
            </div>
            <button
              type="submit"
              className={`w-full text-white button-primary-gradient hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={loading}
            >
              {loading ? "Loading..." : "Log In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
export default LoginForm;
